import React, { Component } from 'react'
import clsx from 'clsx'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import {
  Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent,
  Chip, CircularProgress, Container, CssBaseline, Grid, Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Pagination from '@mui/material/Pagination'
import { withStyles } from '@mui/styles'
import moment from 'moment'
import { FormattedMessage, injectIntl } from 'react-intl'

import Api from '../../services/api'
import ApiReports from '../../services/apireport'
import Api2Talk from 'src/services/api2talk'
import { Styles } from '../../styles/material-styles'
import { Overlay } from '../../styles/global'
import CustomizedSnackbars from '../../components/material-snackbars'
import Footer from '../../components/footer'
import HeaderMenu from '../../components/header-menu'
import MyMaterialTable from '../../components/table'
import ScheduleReportButton from '../../components/report-schedule'
import SimpleAutoComplete from '../../components/auto-complete/autocomplete'
import SimpleAutoCompleteMultiple from '../../components/auto-complete/autocomplete-multiple'
import SimpleCheckBox from '../../components/checkbox/check'
import SimpleDatePicker from '../../components/date-pickers/inline-no-today'

const PAGE_SIZE = 10
const EMPTY_COLUMN_VALUE = '-'
const INVALID_TEXT_VALUES = new Set(['null', 'undefined', 'nan', 'invalid date'])
const NUMERIC_COLUMN_FIELDS = new Set(['ticket', 'message_count'])
const VALID_DATE_FORMATS = [
  moment.ISO_8601,
  'DD/MM/YYYY',
  'DD/MM/YYYY HH:mm:ss',
  'YYYY/MM/DD',
  'YYYY/MM/DD HH:mm:ss',
]

const getStatusChipColor = statusName => {
  if (!statusName) return 'default'
  const name = statusName.toLowerCase()
  if (name.includes('resolv') || name.includes('encerr') || name.includes('finaliz')) return 'success'
  if (name.includes('abandon') || name.includes('cancel') || name.includes('erro')) return 'error'
  if (name.includes('fila') || name.includes('espera')) return 'warning'
  if (name.includes('transfer') || name.includes('humano') || name.includes('atendimento')) return 'primary'
  return 'default'
}

const REPORT_COLUMNS = [
  { titleId: 'conversation.service.report.column.conversation.id', field: 'conversation_id', aliases: ['conversationid', 'id_conversa'] },
  { titleId: 'conversation.service.report.column.date', field: 'date', aliases: ['data', 'created_at', 'data_abertura'] },
  { titleId: 'conversation.service.report.column.organization', field: 'organization', aliases: ['organizationname', 'organizacao', 'organization_name'] },
  { titleId: 'conversation.service.report.column.organization.key', field: 'organization_key', aliases: ['organizationkey', 'chave_organizacao'] },
  { titleId: 'conversation.service.report.column.ticket', field: 'ticket', aliases: ['ticket_id', 'called_id', 'calledid', 'id'] },
  { titleId: 'conversation.service.report.column.requester', field: 'requester', aliases: ['requestername', 'solicitante', 'requesting_user_name', 'requester_name'] },
  { titleId: 'conversation.service.report.column.responsible', field: 'ResponsibleName', aliases: ['responsiblename', 'responsible_name', 'responsavel'] },
  { titleId: 'conversation.service.report.column.parent.category', field: 'parent_category', aliases: ['parentcategoryname', 'categoria_pai', 'parent_category_name'] },
  { titleId: 'conversation.service.report.column.category', field: 'category', aliases: ['categoryname', 'categoria', 'category_name'] },
  { titleId: 'conversation.service.report.column.mexx.tag', field: 'mexx_tag', aliases: ['mexxtag', 'tag_mexx', 'tag_names', 'tags'] },
  {
    titleId: 'conversation.service.report.column.status',
    field: 'status',
    aliases: ['statusname', 'situacao', 'status_name'],
    render: row => (row.status && row.status !== EMPTY_COLUMN_VALUE
      ? <Chip label={row.status} color={getStatusChipColor(row.status)} size="small" />
      : EMPTY_COLUMN_VALUE),
  },
  { titleId: 'conversation.service.report.column.action', field: 'action', aliases: ['acao'] },
  { titleId: 'conversation.service.report.column.waiting.time', field: 'waiting_time', aliases: ['waitingtime', 'tempo_em_espera', 'tempo_espera'] },
  { titleId: 'conversation.service.report.column.human.time', field: 'human_time', aliases: ['humantime', 'tempo_humano'] },
  { titleId: 'conversation.service.report.column.waiting.sla', field: 'waiting_sla', aliases: ['waitingsla', 'sla_espera'] },
  { titleId: 'conversation.service.report.column.solution.sla', field: 'solution_sla', aliases: ['solutionsla', 'sla_solucao', 'tempo_sla_solucao'] },
  { titleId: 'conversation.service.report.column.service.channel', field: 'service_channel', aliases: ['servicechannel', 'canal_de_atendimento', 'canal_atendimento', 'channel'] },
  { titleId: 'conversation.service.report.column.message.count', field: 'message_count', aliases: ['messagecount', 'qtd_mensagens', 'quantidade_mensagens'] },
]

const normalizeColumnValue = (value, field) => {
  if (value === null || value === undefined) return EMPTY_COLUMN_VALUE

  if (field === 'date') {
    const normalizedDate = typeof value === 'string' ? value.trim() : value
    const parsedDate = value instanceof Date
      ? moment(value)
      : moment(normalizedDate, VALID_DATE_FORMATS, true)

    return parsedDate.isValid() && parsedDate.year() > 1
      ? parsedDate.format('DD/MM/YYYY HH:mm:ss')
      : EMPTY_COLUMN_VALUE
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : EMPTY_COLUMN_VALUE
  }

  if (Array.isArray(value)) {
    const validValues = value
      .map(item => normalizeColumnValue(item))
      .filter(item => item !== EMPTY_COLUMN_VALUE)
    return validValues.length ? validValues.join(', ') : EMPTY_COLUMN_VALUE
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim()
    if (!normalizedValue || INVALID_TEXT_VALUES.has(normalizedValue.toLowerCase())) {
      return EMPTY_COLUMN_VALUE
    }

    if (NUMERIC_COLUMN_FIELDS.has(field)) {
      return Number.isFinite(Number(normalizedValue))
        ? normalizedValue
        : EMPTY_COLUMN_VALUE
    }

    return normalizedValue
  }

  if (value instanceof Date) {
    return !Number.isNaN(value.getTime()) && value.getFullYear() > 1
      ? value
      : EMPTY_COLUMN_VALUE
  }

  return EMPTY_COLUMN_VALUE
}

const normalizeReportItem = reportItem => {
  const item = reportItem && typeof reportItem === 'object' && !Array.isArray(reportItem)
    ? reportItem
    : {}
  const itemByLowerKey = Object.entries(item).reduce((normalized, [key, value]) => ({
    ...normalized,
    [key.toLowerCase()]: value,
  }), {})

  return REPORT_COLUMNS.reduce((normalized, column) => {
    const keys = [column.field, ...column.aliases]
    const key = keys.map(candidate => candidate.toLowerCase()).find(candidate => (
      itemByLowerKey[candidate] !== undefined && itemByLowerKey[candidate] !== null
    ))
    return {
      ...normalized,
      [column.field]: normalizeColumnValue(key ? itemByLowerKey[key] : null, column.field),
    }
  }, { ...item })
}

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.errors?.[0]
  || error?.data?.response?.data?.errors?.[0]
  || error?.data?.errors?.[0]
  || fallback
)

class ConversationServiceReport extends Component {
  constructor(props) {
    super(props)

    if (!props.userSession?.user?.userrole) Api.logoff()

    this.defaultFilters = {
      dateStart: moment().format('YYYY/MM/DD HH:mm:ss'),
      dateEnd: moment().format('YYYY/MM/DD HH:mm:ss'),
      groupId: 0,
      OrganizationListFilter: [],
      allOrganizations: false,
      selectedUserTechId: [],
      statusIds: [],
    }

    this.state = {
      ...this.defaultFilters,
      loading: false,
      openNotification: false,
      notificationVariant: 'error',
      notificationMessage: '',
      showGrid: false,
      resultList: [],
      columns: REPORT_COLUMNS.map(({ titleId, field, render }) => ({
        title: props.intl.formatMessage({ id: titleId }),
        field,
        render,
      })),
      totalPages: 0,
      page: 1,
      pageStart: 0,
      pageEnd: 0,
      count: 0,
      groupList: [],
      OrgsList: [],
      fullOrgList: [],
      statusList: [],
      allUsersTech: [],
      scheduleRequest: '',
    }
  }

  componentDidMount() {
    this.loadFilters()
  }

  loadFilters = async () => {
    this.setState({ loading: true })
    await Promise.all([
      this.getGroups(),
      this.getOrganizations(),
      this.getAllStatus(),
      this.getAllUsersTech(),
    ])
    this.setState({ loading: false })
  }

  showError = error => {
    this.setState({
      loading: false,
      openNotification: true,
      notificationVariant: 'error',
      notificationMessage: getErrorMessage(
        error,
        this.props.intl.formatMessage({ id: 'process.error' })
      ),
    })
    Api.kickoff(error)
  }

  getGroups = async () => {
    try {
      const result = await Api.get('/groups')
      this.setState({ groupList: result.data?.data || result.data || [] })
    } catch (error) {
      this.showError(error)
    }
  }

  getOrganizations = async () => {
    try {
      const result = await Api.get('/organizations/inactive-active/tech')
      const response = result.data?.data || result.data || []
      const organizations = response.map(item => {
        const organization = item.organization || item
        return {
          ...organization,
          groupId: organization.groupId
            || organization.groupid
            || organization.GroupId
            || organization.Group?.Id
            || null,
        }
      })
      this.setState({ OrgsList: organizations, fullOrgList: organizations })
    } catch (error) {
      this.showError(error)
    }
  }

  fetchChatStatus = async () => {
    let data = []
    await Api2Talk.get(`/chatstatus`)
      .then(result => {
        data = result.data
      })
      .catch(err => {
        data = err
      })
    return data
  }

  getAllStatus = async () => {
    try {
      const result = await this.fetchChatStatus();
      if (result?.success) {
        this.setState({ statusList: result.data || [] })
      } else {
        this.showError(result)
      }
    } catch (error) {
      this.showError(error)
    }
  }

  getAllUsersTech = async (organizationIds = []) => {
    try {
      const query = organizationIds.length
        ? `?filterOrganizationIds=${organizationIds.join(',')}`
        : ''
      const result = await Api.get(`/users/tech/with-decline-tag-is-only-client${query}`)
      if (result.data?.success) {
        const users = (result.data.data || []).map(user => ({ ...user, name: user.name || '' }))
        this.setState({ allUsersTech: users })
      } else {
        this.showError(result)
      }
    } catch (error) {
      this.showError(error)
    }
  }

  changeValues = async (stateName, value) => {
    if (stateName === 'groupId') {
      const groupId = value?.id || 0
      const organizations = groupId
        ? this.state.fullOrgList.filter(organization => organization.groupId === groupId)
        : this.state.fullOrgList
      this.setState({
        groupId,
        OrgsList: organizations,
        OrganizationListFilter: [],
        allOrganizations: false,
        selectedUserTechId: [],
      })
      await this.getAllUsersTech()
      return
    }

    if (stateName === 'OrganizationListFilter') {
      const organizationIds = (value || []).map(organization => organization.id)
      this.setState({
        OrganizationListFilter: organizationIds,
        allOrganizations: organizationIds.length > 0 && organizationIds.length === this.state.OrgsList.length,
        selectedUserTechId: [],
      })
      await this.getAllUsersTech(organizationIds)
      return
    }

    if (stateName === 'allOrganizations') {
      const organizationIds = value ? this.state.OrgsList.map(organization => organization.id) : []
      this.setState({
        allOrganizations: value,
        OrganizationListFilter: organizationIds,
        selectedUserTechId: [],
      })
      await this.getAllUsersTech(organizationIds)
      return
    }

    this.setState({ [stateName]: value })
  }

  buildReportUrl = (page = 1) => {
    const params = [
      `DateStart=${moment(this.state.dateStart).format('YYYY-MM-DD')}`,
      `DateEnd=${moment(this.state.dateEnd).format('YYYY-MM-DD')}`,
      `Page=${page}`,
      `PageSize=${PAGE_SIZE}`,
    ]

    if (this.state.groupId) params.push(`GroupId=${this.state.groupId}`)

    this.state.OrganizationListFilter.forEach((organizationId, index) => {
      params.push(`OrganizationIds[${index}]=${organizationId}`)
    })

    this.state.selectedUserTechId.forEach((technician, index) => {
      params.push(`TechnicianIds[${index}]=${technician.id}`)
    })

    this.state.statusIds.forEach((status, index) => {
      params.push(`StatusIds[${index}]=${status.id}`)
    })

    return `/report/conversation-service-report/paginate?${params.join('&')}`
  }

  getResultList = async (page = 1) => {
    const dateStart = moment(this.state.dateStart).format('YYYY-MM-DD')
    const dateEnd = moment(this.state.dateEnd).format('YYYY-MM-DD')
    if (dateStart > dateEnd) {
      this.setState({
        openNotification: true,
        notificationVariant: 'error',
        notificationMessage: this.props.intl.formatMessage({
          id: 'report.audit.initial.date.higher.than.end.date',
        }),
      })
      return
    }

    const url = this.buildReportUrl(page)
    this.setState({ loading: true, showGrid: true, scheduleRequest: url })
    try {
      const result = await ApiReports.get(url)
      if (!result.data?.success) {
        this.showError(result)
        return
      }
      const resultList = Array.isArray(result.data.data)
        ? result.data.data.map(normalizeReportItem)
        : []
      const parsedCount = Number(result.data.count)
      const count = Number.isFinite(parsedCount) && parsedCount >= 0
        ? parsedCount
        : resultList.length

      this.setPagination(count, page)
      this.setState({
        resultList,
        loading: false,
      })
    } catch (error) {
      this.showError(error)
    }
  }

  getFile = async () => {
    const data = {
      DateStart: moment(this.state.dateStart).format('YYYY-MM-DD'),
      DateEnd: moment(this.state.dateEnd).format('YYYY-MM-DD'),
      GroupId: this.state.groupId || null,
      OrganizationIds: this.state.OrganizationListFilter,
      TechnicianIds: this.state.selectedUserTechId.map(user => user.id),
      StatusIds: this.state.statusIds.map(status => status.id),
    }

    this.setState({ loading: true })
    try {
      const response = await ApiReports.post('/report/conversation-service-report/excell', data)
      if (!response.data?.success) {
        this.showError(response)
        return
      }
      this.setState({
        loading: false,
        openNotification: true,
        notificationVariant: 'success',
        notificationMessage: `${this.props.intl.formatMessage({ id: 'report.sent' })} (${this.props.userSession.user.email})`,
      })
    } catch (error) {
      this.showError(error)
    }
  }

  setPagination = (count, page) => {
    this.setState({
      count,
      page,
      totalPages: Math.ceil(count / PAGE_SIZE),
      pageStart: count ? ((page - 1) * PAGE_SIZE) + 1 : 0,
      pageEnd: Math.min(page * PAGE_SIZE, count),
    })
  }

  onChangePaginate = (_, page) => this.getResultList(page)

  handleSubmit = event => {
    event.preventDefault()
    this.getResultList(1)
  }

  closeNotification = () => this.setState({ openNotification: false })

  hasChanges = () => {
    const normalizeIds = values => values.map(value => String(value?.id ?? value)).sort()
    const current = {
      dateStart: this.state.dateStart,
      dateEnd: this.state.dateEnd,
      groupId: this.state.groupId,
      OrganizationListFilter: normalizeIds(this.state.OrganizationListFilter),
      allOrganizations: this.state.allOrganizations,
      selectedUserTechId: normalizeIds(this.state.selectedUserTechId),
      statusIds: normalizeIds(this.state.statusIds),
    }
    return JSON.stringify(current) !== JSON.stringify(this.defaultFilters)
  }

  clear = async () => {
    this.setState({
      ...this.defaultFilters,
      OrgsList: this.state.fullOrgList,
      showGrid: false,
      resultList: [],
      scheduleRequest: '',
      totalPages: 0,
      page: 1,
      pageStart: 0,
      pageEnd: 0,
      count: 0,
      loading: true,
    })
    await this.getAllUsersTech()
    this.setState({ loading: false })
  }

  render() {
    const { classes, headerMenu, actionsHeaderMenu, intl } = this.props
    return (
      <div className={classes.root} style={{ paddingLeft: 0, marginLeft: 0 }}>
        <HeaderMenu />
        <main className={clsx(
          classes.content,
          { [classes.contentShift]: headerMenu.open },
          classes.actionsContent,
          { [classes.actionscontentShift]: actionsHeaderMenu.open }
        )}>
          <div className={classes.drawerHeader} />
          <Container component="main" maxWidth="xl" style={{ maxWidth: '100%', paddingLeft: 0, marginLeft: 0 }}>
            <CssBaseline />

            <Grid container spacing={6}>
              <Grid item xs={12}>
                <Card><CardContent className="background-title-search">
                  <Typography variant="h5"><FormattedMessage id="conversation.service.report" /></Typography>
                </CardContent></Card>
              </Grid>
            </Grid>

            <form name="myForm" className={classes.form} onSubmit={this.handleSubmit}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="filters-content" id="filters-header">
                  <Typography className={classes.heading}>{intl.formatMessage({ id: 'filters' })}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <SimpleDatePicker label={intl.formatMessage({ id: 'start.date' })} name="dateStart"
                        stateName="dateStart" changeDate={this.changeValues} currentDate={this.state.dateStart}
                        fullWidth required />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <SimpleDatePicker label={intl.formatMessage({ id: 'end.date' })} name="dateEnd"
                        stateName="dateEnd" changeDate={this.changeValues} currentDate={this.state.dateEnd}
                        fullWidth required />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <SimpleAutoComplete label={intl.formatMessage({ id: 'group' })} options={this.state.groupList} stateName="groupId"
                        selected={this.state.groupList.find(group => group.id === this.state.groupId) || null}
                        changeSelect={this.changeValues} getOptionLabel={option => option.Name || option.name || ''} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <SimpleAutoCompleteMultiple key={this.state.groupId}
                        label={intl.formatMessage({ id: 'organization' })} options={this.state.OrgsList}
                        stateName="OrganizationListFilter"
                        selected={this.state.OrgsList.filter(org => this.state.OrganizationListFilter.includes(org.id))}
                        changeSelect={this.changeValues} />
                      <SimpleCheckBox label={intl.formatMessage({ id: 'select.all.organizations' })}
                        name="allOrganizations" stateName="allOrganizations" selected={this.state.allOrganizations}
                        changeSelect={this.changeValues} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <SimpleAutoCompleteMultiple label={intl.formatMessage({ id: 'assigned' })}
                        options={this.state.allUsersTech} stateName="selectedUserTechId"
                        selected={this.state.selectedUserTechId} changeSelect={this.changeValues} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <SimpleAutoCompleteMultiple label={intl.formatMessage({ id: 'menu.statuses' })}
                        options={this.state.statusList} stateName="statusIds" selected={this.state.statusIds}
                        changeSelect={this.changeValues} />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Grid container style={{ marginBottom: 10 }}>
                <Grid item xs={12} sm={2} style={{ marginRight: 10, marginTop: 10 }}>
                  <Button type="submit" fullWidth variant="contained" color="secondary">
                    <FormattedMessage id="filter" />
                  </Button>
                </Grid>
                {this.state.showGrid && (
                  <Grid item xs={12} sm={5} md={2} style={{ marginRight: 10, marginTop: 10 }}>
                    <Button fullWidth variant="contained" color="secondary" onClick={this.getFile}>
                      <FormattedMessage id="export.plan" />
                    </Button>
                  </Grid>
                )}
                {this.state.showGrid && this.state.scheduleRequest && (
                  <Grid item xs={12} sm={5} md={2} style={{ marginRight: 10, marginTop: 10 }}>
                    <ScheduleReportButton reportKey="conversation-service-report" request={this.state.scheduleRequest} />
                  </Grid>
                )}
                <Grid item xs={12} sm={2} style={{ marginRight: 10, marginTop: 10 }}>
                  <Button type="button" fullWidth variant="contained" color="secondary"
                    disabled={this.state.loading || !this.hasChanges()} onClick={this.clear}>
                    <FormattedMessage id="clear" />
                  </Button>
                </Grid>
              </Grid>
            </form>

            {this.state.showGrid && (
              <>
                <MyMaterialTable title="" columns={this.state.columns} data={this.state.resultList} />
                <Grid container style={{ marginBottom: 12, marginTop: 8 }}>
                  <Grid item xs={12} md={3}>
                    <Pagination count={this.state.totalPages} page={this.state.page} onChange={this.onChangePaginate} />
                  </Grid>
                  <Grid item xs={12} sm={3} style={{ marginTop: 6 }}>
                    {this.state.pageStart} <FormattedMessage id="to" /> {this.state.pageEnd}{' '}
                    <FormattedMessage id="of" /> {this.state.count} <FormattedMessage id="register" />
                  </Grid>
                </Grid>
              </>
            )}

            {this.state.loading && <Overlay><CircularProgress color="secondary" /></Overlay>}
            <CustomizedSnackbars variant={this.state.notificationVariant}
              message={this.state.notificationMessage} isOpen={this.state.openNotification}
              toClose={this.closeNotification} />
          </Container>
        </main>
        <Footer />
      </div>
    )
  }
}

ConversationServiceReport.propTypes = {
  actionsHeaderMenu: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  headerMenu: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  userSession: PropTypes.object.isRequired,
}

const mapStateToProps = state => ({
  actionsHeaderMenu: state.actionsHeaderMenu,
  headerMenu: state.headerMenu,
  userSession: state.userSession,
})

export default injectIntl(connect(mapStateToProps)(withStyles(Styles)(ConversationServiceReport)))
